import { get, isNumber, toNumber, toString, isEqual, identity, negate, isEmpty } from "lodash"
import { createSelector, createSelectorCreator, lruMemoize } from "reselect"
import { getQueryStringState } from "helpers/queryString"

/**
 * Create a selector that uses the lodash `isEqual` function instead of reference equality.
 *
 * The `getQueryStringState` function returns a new object every time it's invoked as it
 * reads the raw query string and parses it into an object. Complex (object) query string
 * parameters will also be returned as new objects, since they are parsed using JSURL.
 *
 * Since `createSelector` uses reference equality by default, selectors for complex parameters
 * will trigger unwanted updates, even when the parameter values haven't changed.
 *
 * https://github.com/reduxjs/reselect#createselectorinputselectors--inputselectors-resultfunc-selectoroptions
 */
const createDeepEqualSelector = createSelectorCreator(lruMemoize, isEqual)

/**
 * Creates a selector that retrieves a value from the query string.
 * @param {string} key - The query string parameter to read.
 * @param {*} defaultValue - The value returned if the query string parameter is not defined.
 * @param {Options} options - `{ as: 'number', overrideParseOptions: { parseBooleans: boolean, parseNumbers: boolean } & ParseOptions }`
 *
 * Note: numeric values are returned as string by default. To force a number, use the `as: 'number'` option.
 */
export const createQueryStringSelector = (key, defaultValue, options) => {
  const getQueryStringValue = () => {
    const rawValue = get(getQueryStringState(get(options, "overrideParseOptions", {})), key)

    if (!rawValue) { return defaultValue }

    if (get(options, "as") === "number") {
      return toNumber(rawValue)
    }

    return isNumber(rawValue) ? toString(rawValue) : rawValue
  }

  // identity check needs to be disabled as function has no actual inputs, which triggers false warning
  return createDeepEqualSelector([getQueryStringValue], identity, { devModeChecks: { identityFunctionCheck: "never" } })
}

export const getRawSelectedModelId = createQueryStringSelector("model")
export const getRawSelectedBase = createQueryStringSelector("base")
export const getSelectedBaseType = createQueryStringSelector("baseType")

export const getIsModelInQueryString = createSelector([getRawSelectedModelId], negate(isEmpty))
